import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Drawer, Text } from '@nextiva/next-ui';

import { CHARGE_SUCCESS_MESSAGE, INVOICE_STATUS } from 'constant';
import { isNotEmptyObj } from 'utils';
import MakePaymentContent from './MakePaymentContent/MakePaymentContent';
import GenericSpinner from 'UIElements/GenericSpinner/GenericSpinner';

import './MakePayment.scss';

const blockName = 'make-payment';

const MakePayment = props => {
  const {
    onClose,
    toggleAddPaymentDrawer,
    amount,
    addPaymentTypeObject,
    clearPaymentTypeObject,
    paymentTypeObject,
    startProcessNextivaBill,
    startDeleteCreditCard,
    startSetDefaultPayment,
    startAddDefaultPaymentMethod,
    startAddNextivaPlan,
    startAddNextivaBill,
    startAddSummaryInvoices,
    startAddNextInvoice,
    billToSiteNumber,
    makePaymentFlags: { invoiceStatus, hasOverage },
    addAlertDetails,
    refreshBillingInfo,
    setSummaryPageFlag,
  } = props;

  const initPayment = () => {
    if (isNotEmptyObj(paymentTypeObject)) {
      return paymentTypeObject.paymentType;
    }
    if (INVOICE_STATUS.OPEN === invoiceStatus) {
      return 'subscription';
    }
    return 'usage';
  };

  const [other, setOther] = useState(
    paymentTypeObject.paymentType === 'other' ? paymentTypeObject.amount : 0.01
  );

  const [payment, setPayment] = useState(initPayment());
  const [showSpinner, setShowSpinner] = useState(false);

  const addCard = useCallback(() => {
    addPaymentTypeObject({
      paymentType: payment,
      amount:
        payment === 'subscription'
          ? amount.totalMonthlyBill
          : payment === 'usage'
          ? amount.usageOverage
          : other,
      payoff: payment === 'other' ? false : true,
    });
    toggleAddPaymentDrawer();
    setSummaryPageFlag(true);
    onClose();
  }, [
    addPaymentTypeObject,
    amount.totalMonthlyBill,
    amount.usageOverage,
    onClose,
    other,
    payment,
    toggleAddPaymentDrawer,
    setSummaryPageFlag,
  ]);

  useEffect(() => {
    clearPaymentTypeObject();
  }, [clearPaymentTypeObject]);

  const processBill = useCallback(async () => {
    try {
      setShowSpinner(true);
      const error = await startProcessNextivaBill({
        corpAcctNbr: props.corpAcctNbr,
        paymentType: payment === 'subscription' ? payment : 'usage',
        amount:
          payment === 'subscription'
            ? amount.totalMonthlyBill
            : payment === 'usage'
            ? amount.usageOverage
            : other,
        payoff: payment !== 'other',
        billToSiteNumber: billToSiteNumber,
      });
      await startAddNextivaBill(billToSiteNumber);
      await startAddSummaryInvoices(billToSiteNumber);
      await startAddNextivaPlan(billToSiteNumber);
      await startAddNextInvoice(billToSiteNumber);
      await clearPaymentTypeObject();

      if (!error) {
        addAlertDetails({ message: CHARGE_SUCCESS_MESSAGE });
        refreshBillingInfo();
      }
    } finally {
      setShowSpinner(false);
      onClose();
    }
  }, [
    billToSiteNumber,
    startProcessNextivaBill,
    props.corpAcctNbr,
    payment,
    amount.totalMonthlyBill,
    amount.usageOverage,
    other,
    startAddNextivaBill,
    startAddSummaryInvoices,
    startAddNextivaPlan,
    startAddNextInvoice,
    clearPaymentTypeObject,
    addAlertDetails,
    onClose,
    refreshBillingInfo,
  ]);

  const setDefaultPayment = useCallback(
    paymentObj => {
      startSetDefaultPayment(paymentObj).then(() =>
        startAddDefaultPaymentMethod(billToSiteNumber)
      );
    },
    [billToSiteNumber, startAddDefaultPaymentMethod, startSetDefaultPayment]
  );

  const onChangePayment = useCallback(
    event => {
      setPayment(event.target.value);
    },
    [setPayment]
  );

  const handleOtherChange = useCallback(
    event => {
      if (
        parseFloat(event.target.value) <= amount.usageOverage &&
        parseFloat(event.target.value) > 0
      ) {
        setOther(event.target.value);
      } else {
        setOther(amount.usageOverage);
      }
    },
    [amount.usageOverage]
  );

  /**
   * Configuration object for payment amount radio button component.
   * Contains amount data, payment options, and change handlers.
   */
  const amountRadioConfig = {
    amount,
    handleOtherChange,
    hasOverage,
    invoiceStatus,
    onChangePayment,
    other,
    payment,
  };

  /**
   * Configuration object for payment methods list component.
   * Contains payment method data, handlers, and account information.
   */
  const paymentListConfig = {
    addAlertDetails,
    billToSiteNumber,
    corpAcctNbr: props.corpAcctNbr,
    isSummaryPage: true,
    paymentMethods: props.paymentMethods,
    setDefaultPayment,
    startDeleteCreditCard,
  };

  /**
   * Configuration object for action buttons component.
   * Contains handlers for card management and bill processing.
   */
  const buttonsConfig = {
    addCard,
    onClose,
    processBill,
    submitButtonDisabled: showSpinner,
  };

  return (
    <Drawer className={`${blockName}`} id={blockName} onClose={onClose}>
      <div className={`${blockName}__drawer-header`}>
        <Text kind="h2">Payment Method</Text>
      </div>
      <div className={`${blockName}__drawer-content`}>
        {showSpinner && <GenericSpinner />}
        <MakePaymentContent
          amountRadioConfig={amountRadioConfig}
          buttonsConfig={buttonsConfig}
          paymentListConfig={paymentListConfig}
          showSpinner={showSpinner}
        />
      </div>
    </Drawer>
  );
};

MakePayment.propTypes = {
  onClose: PropTypes.func.isRequired,
  corpAcctNbr: PropTypes.number.isRequired,
  startDeleteCreditCard: PropTypes.func.isRequired,
  startSetDefaultPayment: PropTypes.func.isRequired,
  startAddNextivaPlan: PropTypes.func.isRequired,
  startAddNextivaBill: PropTypes.func.isRequired,
  startAddSummaryInvoices: PropTypes.func.isRequired,
  startAddNextInvoice: PropTypes.func.isRequired,
  paymentMethods: PropTypes.array.isRequired,
  startAddDefaultPaymentMethod: PropTypes.func.isRequired,
  toggleAddPaymentDrawer: PropTypes.func.isRequired,
  amount: PropTypes.object.isRequired,
  addPaymentTypeObject: PropTypes.func.isRequired,
  clearPaymentTypeObject: PropTypes.func.isRequired,
  paymentTypeObject: PropTypes.object.isRequired,
  startProcessNextivaBill: PropTypes.func.isRequired,
  billToSiteNumber: PropTypes.string.isRequired,
  makePaymentFlags: PropTypes.object.isRequired,
  addAlertDetails: PropTypes.func.isRequired,
  refreshBillingInfo: PropTypes.func.isRequired,
  setSummaryPageFlag: PropTypes.func.isRequired,
};

export default MakePayment;
